/**
 * Centralized API client with secure authentication
 * 
 * This module provides an axios instance configured for secure authentication:
 * - Uses httpOnly cookies for authentication (XSS-resistant)
 * - Falls back to localStorage token for backward compatibility
 * - Automatically handles 401 errors and redirects to login
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with credentials support
const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,  // Send cookies with every request
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add Authorization header as fallback for legacy support
api.interceptors.request.use(
  (config) => {
    // Only add Authorization header if token exists in localStorage
    // The httpOnly cookie will be sent automatically by the browser
    // This provides backward compatibility during migration
    const token = localStorage.getItem('admin_token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear localStorage and redirect to login
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/tothemoon/login')) {
        window.location.href = '/tothemoon/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth-specific functions
export const authApi = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    // Still store in localStorage for backward compatibility
    // But the httpOnly cookie is the primary auth mechanism now
    if (response.data.access_token) {
      localStorage.setItem('admin_token', response.data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(response.data.admin));
    }
    return response;
  },
  
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore errors on logout
      console.warn('Logout API error:', error);
    } finally {
      // Always clear localStorage
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
    }
  },
  
  getMe: () => api.get('/auth/me'),
};

// Export the base URL for components that need it
export const API_URL = `${API_BASE}/api`;

// Export the configured axios instance
export default api;
